import { useState, useEffect, useRef, useCallback } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, MagnifyingGlass, Trash, Upload, X, CloudArrowUp, 
  Image as ImageIcon, Funnel, PencilSimple, MagnifyingGlassPlus, MagnifyingGlassMinus, Users, MapPin, Warning, Package
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import OptimizedImage from "@/components/OptimizedImage";
import { useSettings } from "@/contexts/SettingsContext";
import { PageHeader } from "@/components/PageHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_TYPES = ["PAINÉIS TÉCNICOS", "EXPOSITORES", "PASTAS TÉCNICAS"];

// Product code type priority for sorting
const TYPE_PRIORITY = {
  // 1º - Más importante (agrupados)
  'GRPT': 1,
  'GRPIC': 1,
  'GRLPT': 1,
  'GRLPIC': 1,
  // 2º
  'GRPFXL': 2,
  // 3º
  'GRDK': 3,
  // 4º
  'GRF': 4,
  'GRFXL': 4,
  // 5º
  'GRXL': 5,
  // Otros
  'GRBS': 6,
  'GRDV': 6,
  'GREX': 6,
  'GEX': 6,
  'GRPM': 6
};

// Extract type code from product code (e.g., "B07GRPT0006" -> "GRPT")
const getTypeFromCode = (code) => {
  if (!code) return null;
  // Match patterns - order matters (longer patterns first)
  const match = code.match(/(GRPFXL|GRLPIC|GRFXL|GRPIC|GRLPT|GRPT|GRDK|GRXL|GRBS|GRDV|GREX|GRPM|GRF|GEX)/i);
  return match ? match[1].toUpperCase() : null;
};

// Get priority for a product code (lower = more important)
const getProductPriority = (code) => {
  const typeCode = getTypeFromCode(code);
  return typeCode ? (TYPE_PRIORITY[typeCode] || 99) : 99;
};

// Sort products by priority
const sortProductsByPriority = (products) => {
  return [...products].sort((a, b) => {
    const priorityA = getProductPriority(a.code);
    const priorityB = getProductPriority(b.code);
    if (priorityA !== priorityB) return priorityA - priorityB;
    // If same priority, sort alphabetically by code
    return (a.code || '').localeCompare(b.code || '');
  });
};

export default function Catalog() {
  const navigate = useNavigate();
  const { isReadOnly } = useSettings();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ collection: "", color: "", product_type: "", discontinued: "" });
  const [filterOptions, setFilterOptions] = useState({ collections: [], colors: [], sizes: [], product_types: PRODUCT_TYPES });
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [selectedType, setSelectedType] = useState("PAINÉIS TÉCNICOS");
  const [savingEdit, setSavingEdit] = useState(false);
  const [viewingImage, setViewingImage] = useState(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePan, setImagePan] = useState({ x: 0, y: 0 });
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  // Clients viewing product
  const [showClientsDialog, setShowClientsDialog] = useState(false);
  const [selectedProductClients, setSelectedProductClients] = useState([]);
  const [selectedProductForClients, setSelectedProductForClients] = useState(null);
  const [loadingClients, setLoadingClients] = useState(false);
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const PRODUCTS_PER_PAGE = 40;
  const fileInputRef = useRef(null);
  const editImageInputRef = useRef(null);

  const fetchProducts = useCallback(async (searchTerm = "", filterCollection = "", filterColor = "", filterType = "") => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (filterCollection) params.collection = filterCollection;
      if (filterColor) params.color = filterColor;
      if (filterType) params.product_type = filterType;
      
      const response = await axios.get(`${API}/products`, { params });
      // Sort products by priority
      const sortedProducts = sortProductsByPriority(response.data);
      setProducts(sortedProducts);
    } catch (error) {
      toast.error("Erro ao carregar produtos");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products/filters/options`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  }, []);

  // Bloquear scroll cuando el lightbox está abierto
  useEffect(() => {
    if (viewingImage) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [viewingImage]);

  useEffect(() => {
    fetchProducts("", "", "", "");
    fetchFilterOptions();
  }, [fetchProducts, fetchFilterOptions]);

  const openEditDialog = (product) => {
    setEditingProduct({ ...product });
    setEditImageFile(null);
    setEditImagePreview(null);
    setShowEditDialog(true);
  };

  const handleEditImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setEditImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setEditImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleEditSave = async () => {
    if (!editingProduct) return;
    
    setSavingEdit(true);
    try {
      let newImageUrl = editingProduct.image_url;
      
      // If there's a new image, upload it first
      if (editImageFile) {
        const formData = new FormData();
        formData.append('file', editImageFile);
        formData.append('product_id', editingProduct.id);
        
        const uploadResponse = await axios.post(`${API}/products/${editingProduct.id}/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (uploadResponse.data.image_url) {
          newImageUrl = uploadResponse.data.image_url;
        }
      }
      
      await axios.put(`${API}/products/${editingProduct.id}`, {
        code: editingProduct.code,
        secondary_code: editingProduct.secondary_code,
        collection: editingProduct.collection,
        color: editingProduct.color,
        size: editingProduct.size,
        image_url: newImageUrl,
        filename: editingProduct.filename,
        product_type: editingProduct.product_type,
        value: editingProduct.value ? parseFloat(editingProduct.value) : null,
        is_discontinued: editingProduct.is_discontinued || false
      });
      toast.success("Produto atualizado");
      setShowEditDialog(false);
      setEditImageFile(null);
      setEditImagePreview(null);
      fetchProducts(search, filters.collection, filters.color, filters.product_type);
    } catch (error) {
      toast.error("Erro ao atualizar produto");
    } finally {
      setSavingEdit(false);
    }
  };

  const fetchProductClients = async (product) => {
    setSelectedProductForClients(product);
    setLoadingClients(true);
    setShowClientsDialog(true);
    try {
      const response = await axios.get(`${API}/products/${product.id}/clients`);
      setSelectedProductClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Erro ao carregar clientes");
      setSelectedProductClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    setCurrentPage(1); // Reset to first page when searching
    fetchProducts(value, filters.collection, filters.color, filters.product_type);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value === "all" ? "" : value };
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filtering
    fetchProducts(search, newFilters.collection, newFilters.color, newFilters.product_type);
  };

  const parseFilename = (filename) => {
    const result = {
      code: "",
      secondary_code: null,
      collection: null,
      color: null,
      size: null
    };
    
    const name = filename.includes('.') ? filename.split('.').slice(0, -1).join('.') : filename;
    
    // Extract codes (format: B07GRPIC007, B08GRPIC0007P, etc.)
    const codes = name.match(/B\d{2}[A-Z]{2,}[A-Z0-9]+/g);
    if (codes) {
      result.code = codes[0];
      if (codes.length > 1) result.secondary_code = codes[1];
    }
    
    // Extract size
    const sizeMatch = name.match(/(\d+x\d+(?:\s*R)?)/i);
    if (sizeMatch) result.size = sizeMatch[1].trim();
    
    // Known collections - longer/more specific names FIRST to avoid partial matches
    // e.g., "Soft Stone" must come before "Stone" so it matches correctly
    const collections = [
      "Soft Stone", "Anarchic", "Stone", "Wood", "Cement", 
      "Chromatic", "Classic", "Modern", "Marble", "Concrete",
      "Metal", "Fabric", "Terracotta", "Slate"
    ];
    for (const coll of collections) {
      if (name.toLowerCase().includes(coll.toLowerCase())) {
        result.collection = coll;
        break;
      }
    }
    
    // Known colors
    const colors = ["White", "Fog", "Cream", "Dark", "Grey", "Beige", "Black", "Brown"];
    for (const color of colors) {
      if (name.toLowerCase().includes(color.toLowerCase())) {
        result.color = color;
        break;
      }
    }
    
    return result;
  };

  const handleFileSelect = (files) => {
    const newFiles = Array.from(files).filter(file => 
      file.type.startsWith('image/')
    ).map(file => {
      const parsed = parseFilename(file.name);
      return {
        file,
        preview: URL.createObjectURL(file),
        filename: file.name,
        ...parsed
      };
    });
    
    setUploadFiles(prev => [...prev, ...newFiles]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const removeUploadFile = (index) => {
    setUploadFiles(prev => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const uploadProducts = async () => {
    if (uploadFiles.length === 0) return;
    
    setUploading(true);
    let successCount = 0;
    
    try {
      for (const item of uploadFiles) {
        // Upload image to GridFS first
        const formData = new FormData();
        formData.append('file', item.file);
        
        const uploadResponse = await axios.post(`${API}/products/upload-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        const imageUrl = uploadResponse.data.url;
        
        // Create the product with the GridFS URL
        const productData = {
          code: item.code || `PROD-${Date.now()}`,
          secondary_code: item.secondary_code,
          collection: item.collection,
          color: item.color,
          size: item.size,
          image_url: imageUrl,
          filename: item.filename,
          product_type: selectedType
        };
        
        await axios.post(`${API}/products`, productData);
        successCount++;
      }
      
      toast.success(`${successCount} produto(s) adicionado(s) ao catálogo`);
      setShowUploadDialog(false);
      setUploadFiles([]);
      fetchProducts(search, filters.collection, filters.color, filters.product_type);
      fetchFilterOptions();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Erro ao carregar produtos");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/products/${deleteId}`);
      toast.success("Produto eliminado");
      setDeleteId(null);
      fetchProducts(search, filters.collection, filters.color, filters.product_type);
    } catch (error) {
      toast.error("Erro ao eliminar produto");
    }
  };

  const openUploadDialog = () => {
    setUploadFiles([]);
    setShowUploadDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size={40} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="catalog-page">
      {/* Header */}
      <PageHeader
        icon={Package}
        title="Catálogo de Produtos"
        actions={!isReadOnly && (
          <Button
            className="btn-primary"
            onClick={openUploadDialog}
            data-testid="btn-upload-products"
          >
            <Upload size={18} />
            Carregar Produtos
          </Button>
        )}
      />

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="search-input-container flex-1 max-w-md">
          <MagnifyingGlass size={18} />
          <Input
            placeholder="Pesquisar por código, coleção..."
            value={search}
            onChange={handleSearch}
            className="gresart-input pl-10"
            data-testid="search-products"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={filters.product_type || "all"} onValueChange={(v) => handleFilterChange("product_type", v)}>
            <SelectTrigger className="w-[200px]" data-testid="filter-type">
              <Funnel size={14} className="mr-1" />
              <SelectValue placeholder="Tipologia" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tipologia</SelectItem>
              {filterOptions.product_types.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.collection || "all"} onValueChange={(v) => handleFilterChange("collection", v)}>
            <SelectTrigger className="w-[140px]" data-testid="filter-collection">
              <SelectValue placeholder="Series" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Series</SelectItem>
              {filterOptions.collections.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.discontinued || "all"} onValueChange={(v) => handleFilterChange("discontinued", v)}>
            <SelectTrigger className="w-[160px]" data-testid="filter-discontinued">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="discontinued">Descontinuados</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Products Grid */}
      {products.length === 0 ? (
        <div className="gresart-card">
          <div className="empty-state">
            <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mb-4">
              <ImageIcon size={32} className="text-stone-400" />
            </div>
            <h3 className="text-lg font-medium text-stone-900 mb-2">
              {search || filters.collection 
                ? "Nenhum produto encontrado" 
                : "Catálogo vazio"}
            </h3>
            <p className="text-stone-500 mb-4">
              {search || filters.collection 
                ? "Tente outros filtros" 
                : "Carregue imagens de produtos para começar"}
            </p>
            {!search && !filters.collection && (
              <Button className="btn-primary" onClick={openUploadDialog} data-testid="btn-upload-first">
                <Upload size={18} />
                Carregar Produtos
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Group products by type - PAINÉIS TÉCNICOS first, sorted by priority */}
          {["PAINÉIS TÉCNICOS", "EXPOSITORES", "PASTAS TÉCNICAS"].map(productType => {
            // Filter by product type and discontinued status
            let typeProducts = products.filter(p => p.product_type === productType);
            
            // Apply discontinued filter
            if (filters.discontinued === "active") {
              typeProducts = typeProducts.filter(p => !p.is_discontinued);
            } else if (filters.discontinued === "discontinued") {
              typeProducts = typeProducts.filter(p => p.is_discontinued);
            }
            
            if (typeProducts.length === 0) return null;
            
            // Pagination logic per type
            const totalPages = Math.ceil(typeProducts.length / PRODUCTS_PER_PAGE);
            const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
            const paginatedProducts = typeProducts.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);
            
            return (
              <div key={productType}>
                <div className="flex items-center justify-between mb-4 pb-2 border-b border-stone-200">
                  <h2 className="text-lg font-semibold font-[Manrope] text-stone-800">
                    {productType} <span className="text-stone-400 font-normal">({typeProducts.length})</span>
                  </h2>
                  {/* Pagination controls */}
                  {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-stone-500">
                        Página {currentPage} de {totalPages}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                          className="px-2 py-1 text-xs bg-stone-100 hover:bg-stone-200 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                        >
                          ««
                        </button>
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="px-2 py-1 text-xs bg-stone-100 hover:bg-stone-200 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                        >
                          «
                        </button>
                        <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="px-2 py-1 text-xs bg-stone-100 hover:bg-stone-200 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                        >
                          »
                        </button>
                        <button
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                          className="px-2 py-1 text-xs bg-stone-100 hover:bg-stone-200 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                        >
                          »»
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="product-grid stagger-animation">
                  {paginatedProducts.map((product) => (
            <div key={product.id} className={`product-card group ${product.is_discontinued ? 'ring-2 ring-stone-800 bg-stone-100' : ''}`} data-testid={`product-card-${product.id}`}>
              <div 
                className="product-image-container cursor-pointer relative"
                onClick={() => setViewingImage(product)}
              >
                <OptimizedImage 
                  src={product.image_url} 
                  alt={product.code}
                  width={300}
                />
                {/* Icons positioned absolute relative to product-image-container */}
                <div className="absolute top-2 right-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-all z-10">
                  <button
                    onClick={(e) => { e.stopPropagation(); fetchProductClients(product); }}
                    className="p-1.5 bg-white/90 hover:bg-blue-50 text-stone-600 hover:text-blue-600 rounded-full shadow-sm"
                    data-testid={`btn-clients-product-${product.id}`}
                    title="Ver clientes com este produto"
                  >
                    <MapPin size={16} />
                  </button>
                  {!isReadOnly && (
                  <>
                  <button
                    onClick={(e) => { e.stopPropagation(); openEditDialog(product); }}
                    className="p-1.5 bg-white/90 hover:bg-teal-50 text-stone-600 hover:text-[#00A19A] rounded-full shadow-sm"
                    data-testid={`btn-edit-product-${product.id}`}
                    title="Editar produto"
                  >
                    <PencilSimple size={16} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteId(product.id); }}
                    className="p-1.5 bg-white/90 hover:bg-red-50 text-stone-600 hover:text-red-500 rounded-full shadow-sm"
                    data-testid={`btn-delete-product-${product.id}`}
                    title="Eliminar produto"
                  >
                    <Trash size={16} />
                  </button>
                  </>
                  )}
                </div>
                <div className="product-card-overlay pointer-events-none" />
              </div>
              <div className="product-info">
                <div className="product-info-content flex flex-col items-center text-center">
                  {product.product_type && (
                    <span className="inline-block text-[10px] font-medium text-[#00A19A] bg-[#00A19A]/10 px-2 py-0.5 rounded mb-2">
                      {product.product_type}
                    </span>
                  )}
                  <p className="code-badge text-xs mb-2">{product.code}</p>
                  <div className="flex flex-wrap items-center justify-center gap-1 text-xs text-stone-500">
                    {product.collection && <span className="bg-stone-100 px-1.5 py-0.5 rounded">{product.collection}</span>}
                    {product.color && <span className="bg-stone-100 px-1.5 py-0.5 rounded">{product.color}</span>}
                  </div>
                  {product.size && (
                    <p className={`text-xs mt-1 font-medium ${product.is_discontinued ? 'text-stone-700 bg-white px-1.5 py-0.5 rounded inline-block' : 'text-stone-400'}`}>{product.size}</p>
                  )}
                </div>
                {/* Bottom row: Discontinued badge on left, price on right */}
                <div className="flex items-center justify-between mt-auto pt-2">
                  {product.is_discontinued ? (
                    <span className="px-2 py-1 text-xs rounded font-medium bg-stone-800 text-white">
                      Descontinuado
                    </span>
                  ) : (
                    <span></span>
                  )}
                  {product.value && (
                    <p className="text-sm font-semibold text-[#00A19A]">€{parseFloat(product.value).toFixed(2)}</p>
                  )}
                </div>
              </div>
            </div>
                  ))}
                </div>
                {/* Bottom Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-stone-100">
                    <span className="text-sm text-stone-500">
                      Página {currentPage} de {totalPages}
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="px-2 py-1 text-xs bg-stone-100 hover:bg-stone-200 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                      >
                        ««
                      </button>
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-2 py-1 text-xs bg-stone-100 hover:bg-stone-200 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                      >
                        «
                      </button>
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-2 py-1 text-xs bg-stone-100 hover:bg-stone-200 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                      >
                        »
                      </button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="px-2 py-1 text-xs bg-stone-100 hover:bg-stone-200 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                      >
                        »»
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Carregar Produtos para o Catálogo</DialogTitle>
          </DialogHeader>
          
          {/* Product Type Selection */}
          <div className="mb-4">
            <Label className="mb-2 block">Tipologia do Produto</Label>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-full" data-testid="select-product-type">
                <SelectValue placeholder="Selecionar tipologia" />
              </SelectTrigger>
              <SelectContent>
                {PRODUCT_TYPES.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Drop Zone */}
          <div
            className={`upload-area ${dragging ? "dragging" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files)}
              data-testid="file-input"
            />
            <CloudArrowUp size={48} className="mx-auto text-stone-400 mb-4" />
            <p className="text-stone-700 font-medium">Arraste imagens aqui ou clique para selecionar</p>
            <p className="text-sm text-stone-500 mt-2">
              Os códigos são extraídos automaticamente do nome do ficheiro
            </p>
            <p className="text-xs text-stone-400 mt-1">
              Exemplo: <span className="font-mono">B07GRPIC007 Anarchic White 60x60.jpg</span>
            </p>
          </div>

          {/* File Preview */}
          {uploadFiles.length > 0 && (
            <div className="flex-1 overflow-auto mt-4">
              <Label className="mb-2 block">Ficheiros a carregar ({uploadFiles.length})</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[300px] overflow-y-auto pr-2">
                {uploadFiles.map((item, index) => (
                  <div key={index} className="relative border border-stone-200 rounded-lg overflow-hidden" data-testid={`upload-preview-${index}`}>
                    <img 
                      src={item.preview} 
                      alt={item.filename}
                      className="w-full aspect-square object-cover"
                    />
                    <button
                      onClick={() => removeUploadFile(index)}
                      className="absolute top-1 right-1 p-1 bg-white/90 hover:bg-red-50 text-stone-600 hover:text-red-500 rounded-full"
                    >
                      <X size={14} />
                    </button>
                    <div className="p-2 bg-white">
                      <p className="code-badge text-xs truncate">{item.code || "Sem código"}</p>
                      <p className="text-xs text-stone-400 truncate mt-1">{item.filename}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancelar
            </Button>
            <Button 
              className="btn-primary" 
              onClick={uploadProducts}
              disabled={uploadFiles.length === 0 || uploading}
              data-testid="btn-confirm-upload"
            >
              {uploading ? "A carregar..." : `Carregar ${uploadFiles.length} produto(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar produto?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O produto será eliminado do catálogo e de todos os showrooms.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirm-delete-product">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Product Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        setShowEditDialog(open);
        if (!open) {
          setEditImageFile(null);
          setEditImagePreview(null);
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Editar Produto</DialogTitle>
          </DialogHeader>
          {editingProduct && (
            <div className="grid grid-cols-3 gap-4 py-2">
              {/* Left Column - Image */}
              <div className="flex flex-col items-center gap-2">
                <div className="relative group">
                  <img 
                    src={editImagePreview || editingProduct.image_url} 
                    alt={editingProduct.code}
                    className="h-40 w-auto object-contain bg-stone-50 rounded-lg border border-stone-200"
                  />
                  <button
                    type="button"
                    onClick={() => editImageInputRef.current?.click()}
                    className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                  >
                    <div className="text-white text-xs font-medium flex flex-col items-center gap-1">
                      <CloudArrowUp size={20} />
                      <span>Alterar</span>
                    </div>
                  </button>
                </div>
                <input
                  ref={editImageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleEditImageSelect}
                  className="hidden"
                />
                {editImageFile && (
                  <p className="text-xs text-green-600">✓ Nova imagem</p>
                )}
              </div>
              
              {/* Middle Column - Main fields */}
              <div className="space-y-3">
                <div>
                  <Label htmlFor="edit-code" className="text-xs">Código *</Label>
                  <Input
                    id="edit-code"
                    value={editingProduct.code}
                    onChange={(e) => setEditingProduct({ ...editingProduct, code: e.target.value })}
                    className="gresart-input mt-1 h-8 text-sm"
                    data-testid="input-edit-code"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-secondary-code" className="text-xs">Código Secundário</Label>
                  <Input
                    id="edit-secondary-code"
                    value={editingProduct.secondary_code || ""}
                    onChange={(e) => setEditingProduct({ ...editingProduct, secondary_code: e.target.value })}
                    className="gresart-input mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-type" className="text-xs">Tipologia</Label>
                  <Select 
                    value={editingProduct.product_type || "PAINÉIS TÉCNICOS"} 
                    onValueChange={(v) => setEditingProduct({ ...editingProduct, product_type: v })}
                  >
                    <SelectTrigger className="mt-1 h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRODUCT_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit-collection" className="text-xs">Coleção</Label>
                  <Input
                    id="edit-collection"
                    value={editingProduct.collection || ""}
                    onChange={(e) => setEditingProduct({ ...editingProduct, collection: e.target.value })}
                    className="gresart-input mt-1 h-8 text-sm"
                  />
                </div>
              </div>
              
              {/* Right Column - Other fields */}
              <div className="space-y-3">
                <div>
                  <Label htmlFor="edit-color" className="text-xs">Cor</Label>
                  <Input
                    id="edit-color"
                    value={editingProduct.color || ""}
                    onChange={(e) => setEditingProduct({ ...editingProduct, color: e.target.value })}
                    className="gresart-input mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-size" className="text-xs">Tamanho</Label>
                  <Input
                    id="edit-size"
                    value={editingProduct.size || ""}
                    onChange={(e) => setEditingProduct({ ...editingProduct, size: e.target.value })}
                    className="gresart-input mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-value" className="text-xs">Valor (€)</Label>
                  <Input
                    id="edit-value"
                    type="number"
                    step="0.01"
                    min="0"
                    value={editingProduct.value || ""}
                    onChange={(e) => setEditingProduct({ ...editingProduct, value: e.target.value })}
                    className="gresart-input mt-1 h-8 text-sm"
                    placeholder="0.00"
                    data-testid="input-edit-value"
                  />
                </div>
                {/* Discontinued Toggle */}
                <div className="flex items-center justify-between p-2 bg-stone-100 border border-stone-300 rounded-lg">
                  <div>
                    <Label className="text-xs font-medium text-stone-800">Descontinuado</Label>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditingProduct({ ...editingProduct, is_discontinued: !editingProduct.is_discontinued })}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      editingProduct.is_discontinued ? 'bg-stone-800' : 'bg-stone-300'
                    }`}
                    data-testid="toggle-discontinued"
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      editingProduct.is_discontinued ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" size="sm" onClick={() => setShowEditDialog(false)}>
              Cancelar
            </Button>
            <Button type="button" className="btn-primary" size="sm" onClick={handleEditSave} disabled={savingEdit} data-testid="btn-save-edit">
              {savingEdit ? "A guardar..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox - Full screen modal with zoom and pan */}
      <Dialog open={!!viewingImage} onOpenChange={() => { setViewingImage(null); setImageZoom(1); setImagePan({ x: 0, y: 0 }); }}>
        <DialogContent 
          className="max-w-[95vw] max-h-[95vh] p-4 bg-black/95 border-none"
          onWheel={(e) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.2 : 0.2;
            setImageZoom(prev => Math.min(Math.max(prev + delta, 0.5), 5));
          }}
        >
          {/* Hint */}
          <div className="absolute top-4 left-4 text-white/50 text-xs z-10">
            Scroll para zoom • Arrastra para mover
          </div>
          
          <div className="flex flex-col items-center justify-center">
            {viewingImage?.image_url && (
              <div 
                className="bg-white rounded-lg p-2 max-w-full overflow-hidden cursor-grab active:cursor-grabbing"
                onMouseDown={(e) => {
                  if (imageZoom > 1) {
                    e.preventDefault();
                    setIsDraggingImage(true);
                    setDragStartPos({ x: e.clientX - imagePan.x, y: e.clientY - imagePan.y });
                  }
                }}
                onMouseMove={(e) => {
                  if (isDraggingImage && imageZoom > 1) {
                    setImagePan({
                      x: e.clientX - dragStartPos.x,
                      y: e.clientY - dragStartPos.y
                    });
                  }
                }}
                onMouseUp={() => setIsDraggingImage(false)}
                onMouseLeave={() => setIsDraggingImage(false)}
              >
                <img 
                  src={viewingImage.image_url} 
                  alt={viewingImage.code || 'Product'}
                  className="max-h-[55vh] max-w-full object-contain block select-none"
                  style={{ 
                    transform: `scale(${imageZoom}) translate(${imagePan.x / imageZoom}px, ${imagePan.y / imageZoom}px)`,
                    transition: isDraggingImage ? 'none' : 'transform 0.1s ease-out'
                  }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                  draggable={false}
                />
              </div>
            )}
            
            {/* Zoom controls */}
            <div className="mt-4 flex items-center gap-2 bg-white/10 rounded-full p-1">
              <button
                onClick={() => { setImageZoom(prev => Math.max(prev - 0.5, 0.5)); setImagePan({ x: 0, y: 0 }); }}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                disabled={imageZoom <= 0.5}
              >
                <MagnifyingGlassMinus size={20} className="text-white" />
              </button>
              <span className="text-white text-sm px-3 min-w-[60px] text-center">{Math.round(imageZoom * 100)}%</span>
              <button
                onClick={() => setImageZoom(prev => Math.min(prev + 0.5, 5))}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                disabled={imageZoom >= 5}
              >
                <MagnifyingGlassPlus size={20} className="text-white" />
              </button>
              {/* Reset button */}
              <button
                onClick={() => { setImageZoom(1); setImagePan({ x: 0, y: 0 }); }}
                className="p-2 hover:bg-white/20 rounded-full transition-colors ml-2 text-white text-xs"
                title="Reset"
              >
                100%
              </button>
            </div>
            
            <div className="mt-3 text-center">
              <p className="text-xl font-bold text-white">{viewingImage?.code}</p>
              {viewingImage?.value && (
                <p className="text-2xl font-bold text-[#00A19A] mt-2">€{parseFloat(viewingImage.value || 0).toFixed(2)}</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Clients with Product Dialog */}
      <Dialog open={showClientsDialog} onOpenChange={setShowClientsDialog}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-[Manrope] flex items-center gap-2">
              <Users size={20} className="text-[#009392]" />
              Clientes com este Produto
            </DialogTitle>
          </DialogHeader>
          
          {selectedProductForClients && (
            <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg mb-3">
              <OptimizedImage 
                src={selectedProductForClients.image_url}
                alt={selectedProductForClients.code}
                className="w-12 h-12 object-contain rounded"
                width={48}
              />
              <div>
                <p className="font-semibold text-stone-800">{selectedProductForClients.code}</p>
                <p className="text-xs text-stone-500">{selectedProductForClients.collection}</p>
              </div>
            </div>
          )}
          
          <div className="flex-1 overflow-y-auto">
            {loadingClients ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner size={32} />
              </div>
            ) : selectedProductClients.length === 0 ? (
              <div className="text-center py-8 text-stone-400">
                <Users size={40} className="mx-auto mb-2 opacity-50" />
                <p>Nenhum cliente tem este produto</p>
              </div>
            ) : (
              <div className="space-y-2">
                {selectedProductClients.map((client) => (
                  <div 
                    key={client.id}
                    className="flex items-center gap-3 p-3 bg-white border border-stone-100 rounded-lg hover:border-[#009392] hover:shadow-sm cursor-pointer transition-all"
                    onClick={() => {
                      setShowClientsDialog(false);
                      navigate(`/clients/${client.id}`);
                    }}
                  >
                    {client.logo_url ? (
                      <OptimizedImage 
                        src={client.logo_url}
                        alt={client.name}
                        className="w-10 h-10 object-contain rounded"
                        width={40}
                      />
                    ) : (
                      <div className="w-10 h-10 bg-stone-100 rounded flex items-center justify-center">
                        <Users size={20} className="text-stone-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-800 truncate">{client.name}</p>
                      {client.city && (
                        <p className="text-xs text-stone-400 flex items-center gap-1">
                          <MapPin size={10} />
                          {client.city}
                        </p>
                      )}
                    </div>
                    {client.panel_sizes && client.panel_sizes.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {client.panel_sizes.map((size, i) => (
                          <span key={i} className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                            {size}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter className="mt-4">
            <p className="text-xs text-stone-400">
              {selectedProductClients.length} {selectedProductClients.length === 1 ? 'cliente' : 'clientes'}
            </p>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
